// ── Duplicar, borrar, mover y convertir, sobre el DOM de la vista previa ─────
//
// El servidor hace lo mismo sobre el archivo cuando se guarda. Acá se hace sobre
// el iframe para que se vea al instante, y para que deshacer no tenga que ir y
// volver al servidor.
//
// Sin React: lo que puede equivocarse es qué nodo se toca, qué ids quedan y
// cómo se vuelve atrás. Eso se prueba sin montar nada.

/** Los ids que se generan acá tienen que pasar la validación del servidor. */
const ID_ALFABETO = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/**
 * Un id nuevo que no choque con ninguno de la página.
 *
 * Los genera el cliente y viajan con la operación. Si los inventara el servidor,
 * el navegador no sabría con qué id quedó cada copia, y editar el duplicado
 * antes de guardar escribiría sobre un id que no existe.
 */
export function newEvoId(usados: ReadonlySet<string>): string {
  for (let intento = 0; intento < 100; intento++) {
    let s = 'e_';
    for (let i = 0; i < 7; i++) {
      s += ID_ALFABETO[Math.floor(Math.random() * ID_ALFABETO.length)];
    }
    if (!usados.has(s)) return s;
  }
  // Cien colisiones seguidas con 62^7 posibilidades no pasa; si pasara, es mejor
  // un id largo y feo que devolver uno repetido.
  return `e_${String(usados.size)}${ID_ALFABETO.slice(0, 4)}`;
}

/** Todos los ids estampados que hay en el documento. */
export function idsEnUso(doc: Document): Set<string> {
  const out = new Set<string>();
  for (const el of doc.querySelectorAll('[data-evo-id]')) {
    const id = el.getAttribute('data-evo-id');
    if (id) out.add(id);
  }
  return out;
}

export interface DuplicateResult {
  clone: HTMLElement;
  /** id viejo → id nuevo, para el elemento y cada descendiente estampado. */
  idMap: Record<string, string>;
}

/**
 * Copia el elemento con todo lo que contiene y la inserta justo después.
 *
 * Cada id del subárbol copiado se reemplaza por uno nuevo: dos elementos con el
 * mismo id harían que cualquier edición posterior apuntara a los dos.
 */
export function duplicateElement(el: HTMLElement): DuplicateResult | null {
  const doc = el.ownerDocument;
  const padre = el.parentElement;
  if (!doc || !padre) return null;

  const clone = el.cloneNode(true) as HTMLElement;
  const usados = idsEnUso(doc);
  const idMap: Record<string, string> = {};

  const conId = [clone, ...Array.from(clone.querySelectorAll('[data-evo-id]'))];
  for (const nodo of conId) {
    const viejo = nodo.getAttribute('data-evo-id');
    if (!viejo) continue;
    const nuevo = newEvoId(usados);
    usados.add(nuevo);
    idMap[viejo] = nuevo;
    nodo.setAttribute('data-evo-id', nuevo);
  }
  // Un duplicado no arrastra el estado de edición del original.
  clone.removeAttribute('contenteditable');
  for (const nodo of Array.from(clone.querySelectorAll('[contenteditable]'))) {
    nodo.removeAttribute('contenteditable');
  }

  el.after(clone);
  return { clone, idMap };
}

/** Lo necesario para volver a poner un elemento donde estaba. */
export interface RemovedSnapshot {
  html: string;
  parentId: string | null;
  /** id del hermano que quedaba después, o null si era el último. */
  nextId: string | null;
}

export function removeElement(el: HTMLElement): RemovedSnapshot | null {
  const padre = el.parentElement;
  if (!padre) return null;
  const siguiente = el.nextElementSibling;
  const snap: RemovedSnapshot = {
    html: el.outerHTML,
    parentId: padre.getAttribute('data-evo-id'),
    nextId: siguiente?.getAttribute('data-evo-id') ?? null,
  };
  el.remove();
  return snap;
}

/**
 * Vuelve a poner un elemento borrado en su lugar.
 *
 * Se ubica por el id del hermano siguiente, no por índice: entre el borrado y el
 * deshacer pueden haber pasado otras cosas, y un índice viejo lo dejaría en
 * cualquier lado.
 */
export function restoreElement(doc: Document, snap: RemovedSnapshot): HTMLElement | null {
  const padre = snap.parentId
    ? doc.querySelector(`[data-evo-id="${snap.parentId}"]`)
    : doc.body;
  if (!padre) return null;

  const tmp = doc.createElement('div');
  tmp.innerHTML = snap.html;
  const el = tmp.firstElementChild as HTMLElement | null;
  if (!el) return null;

  const siguiente = snap.nextId ? doc.querySelector(`[data-evo-id="${snap.nextId}"]`) : null;
  if (siguiente && siguiente.parentElement === padre) padre.insertBefore(el, siguiente);
  else padre.appendChild(el);
  return el;
}

/** Intercambia el elemento con su hermano anterior o siguiente. */
export function moveElement(el: HTMLElement, direction: 'up' | 'down'): boolean {
  const vecino = direction === 'up' ? el.previousElementSibling : el.nextElementSibling;
  if (!vecino) return false;
  if (direction === 'up') vecino.before(el);
  else vecino.after(el);
  return true;
}

/** ¿Tiene hermano hacia ese lado? Es lo que decide si el botón se puede apretar. */
export function canMove(el: HTMLElement, direction: 'up' | 'down'): boolean {
  return (direction === 'up' ? el.previousElementSibling : el.nextElementSibling) !== null;
}

/**
 * Convierte un párrafo en encabezado o al revés.
 *
 * Se construye un elemento nuevo con los mismos atributos y el mismo contenido:
 * no se puede cambiar la etiqueta de un nodo que ya existe. El data-evo-id viaja
 * con los atributos, así que la identidad se conserva.
 */
export function convertTag(el: HTMLElement, tag: string): HTMLElement | null {
  const doc = el.ownerDocument;
  const padre = el.parentElement;
  if (!doc || !padre) return null;
  if (el.tagName.toLowerCase() === tag) return el;

  const nuevo = doc.createElement(tag);
  for (const attr of Array.from(el.attributes)) nuevo.setAttribute(attr.name, attr.value);
  nuevo.innerHTML = el.innerHTML;
  // La edición no sobrevive al reemplazo: el nodo que tenía el cursor ya no está.
  nuevo.removeAttribute('contenteditable');
  padre.replaceChild(nuevo, el);
  return nuevo;
}
